import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { ArrowLeft, Mail } from "lucide-react";
import { showError, showSuccess } from "@/shared/utils/toast-utils";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { useForgotPassword } from "@/app/auth/services/auth";
import orbiusLogo from "@/assets/orbius_logo.png";
import launchBetterImage from "@/assets/Launch_better.jpg";
import { ROUTES } from "@/config/routeConfig";

const ForgotPasswordPage = () => {
  const forgotPassword = useForgotPassword();
  const [email, setEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      showError(ERROR.PASSWORD.MISSING_EMAIL);
      return;
    }

    try {
      await forgotPassword.mutateAsync(email);
      setIsEmailSent(true);
      showSuccess(SUCCESS.PASSWORD.RESET_EMAIL_SENT);
    } catch (error) {
      showError(ERROR.PASSWORD.FORGOT_FAILED);
    }
  };

  return (
    <>
      <div className="relative min-h-screen overflow-hidden bg-muted">
        <img
          src={launchBetterImage}
          alt="Launch Better"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right -scale-x-100" />
        <div className="relative z-10 flex min-h-screen w-full flex-col p-5 gap-5">
          <div className="flex w-full items-center justify-between">
            <img src={orbiusLogo} alt="Orbius Logo" className="h-16 w-auto drop-shadow-md" />

            <Button
              variant="outline"
              onClick={() => window.open('mailto:support@orbius.com', '_blank')}
            >
              Contact us
            </Button>
          </div>

          <div className="grid grid-cols-2 flex-1 items-center justify-center">
            <div className="w-full h-full">
            </div>
            <div className="flex items-center justify-center w-full h-full">
              {isEmailSent ? (
                <>
                  {emailSentBody()}
                </>
              ) : (
                <>
                  {/* Main Content */}
                  {mainBody()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  function mainBody() {
    return (
      <Card className="w-full max-w-md border shadow-sm">
        <CardHeader>
          <CardTitle>
            Forgot Password?
          </CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
                required />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={forgotPassword.isPending}
            >
              {forgotPassword.isPending ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link to={ROUTES.LOGIN}>
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  function emailSentBody() {
    return (
      <Card className="w-full max-w-md border shadow-sm">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <Mail className="h-6 w-6 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-semibold">
            Check Your Email
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p>
            We've sent a password reset link to <strong>{email}</strong>
          </p>
          <p className="text-sm">
            Please check your email and click the link to reset your password.
            The link will expire in 15 minutes.
          </p>
          <div className="pt-4">
            <Link to={ROUTES.LOGIN}>
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => setIsEmailSent(false)}
            >
              Didn't receive the email? Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
};

export default ForgotPasswordPage;
